import { CallGroupUsage } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { useQuery } from 'react-query';
import { CallGroupApi } from '@frontend/api-call-group';
import { Trans, useTranslation } from '@frontend/i18n';
import { useNonReactiveInterRouter, useSettingsNavigate } from '@frontend/settings-routing';
import { ModalControlModalProps, Text, useAlert, useModalControl } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { logError } from '../../utils/loggers';
import { DeleteFlow } from '../common/delete-flow';
import { useCallGroupMutations } from './use-call-group-mutations';

export const DeleteCallGroupIconButton = ({
  name,
  id,
  phoneTenantId,
}: {
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();

  return (
    <DeleteFlow.IconButton
      label={t('Delete Call Group')}
      triggerProps={triggerProps}
      trackingId={trackingId({ context: 'setting', feature: 'call-groups', details: 'index-delete' })}
    >
      <CallGroupDeleteModal modalProps={modalProps} name={name} id={id} phoneTenantId={phoneTenantId} />
    </DeleteFlow.IconButton>
  );
};

export const CallGroupDeleteModal = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: queryKeys.callGroupUsageV2(id),
    queryFn: () => CallGroupApi.getCallGroupUsageV2({ callGroupId: id }),
    enabled: modalProps.show,
    staleTime: 0,
    meta: {
      error: {
        message:
          'CallGroupDeleteModal:callGroupUsageV2Query | Failed to fetch call group usage v2 when deleting call group',
        data: { callGroupId: id, phoneTenantId: phoneTenantId },
      },
    },
  });

  return (
    <DeleteFlow.Modal
      CanDeleteContent={CallGroupCanDeleteContent}
      CannotDeleteContent={CallGroupCannotDeleteContentV2}
      id={id}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      modalProps={modalProps}
      name={name}
      phoneTenantId={phoneTenantId}
      usage={data?.usages || []}
    />
  );
};

const CallGroupCanDeleteContent = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { remove } = useCallGroupMutations(phoneTenantId);
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { navigate } = useSettingsNavigate();
  const nonReactiveInterRouter = useNonReactiveInterRouter();

  const removeFn = () =>
    remove.mutate(
      { callGroupId: id },
      {
        onSuccess: () => {
          alerts.success(t('Call Group deleted successfully.'));
          modalProps.onClose();
          // Clear any pending navigation blocker (e.g. from unsaved call group edits) since the
          // call group no longer exists and there is nothing left to warn the user about.
          nonReactiveInterRouter.getState().setShouldBlockFn(undefined);
          navigate({ to: '/phone/call-groups' });
        },
        onError: (error) => {
          alerts.error(t('System failed to delete Call Group. Please try again.'));
          logError('CallGroupDeleteModal:removeCallGroupMutation | Failed to delete call group', {
            data: { phoneTenantId: phoneTenantId, callGroupId: id, error },
          });
        },
      }
    );

  return (
    <DeleteFlow.CanDeleteContent
      modalProps={modalProps}
      name={name}
      title={t('Delete Call Group?')}
      removeFn={removeFn}
    />
  );
};

const CallGroupCannotDeleteContentV2 = ({
  id,
  modalProps,
  name,
  usage,
  disableLinks = false,
}: {
  id: string;
  modalProps: ModalControlModalProps;
  name: string;
  usage: CallGroupUsage[];
  disableLinks?: boolean;
}) => {
  const { t } = useTranslation('phone');

  const usageCount = usage.length;

  return (
    <DeleteFlow.CannotDeleteContentV2
      id={id}
      showTable={true}
      modalProps={modalProps}
      disableLinks={disableLinks}
      usages={usage}
      title={t('Cannot Delete Call Group')}
      body={
        <Trans t={t} name={name}>
          <Text>
            <Text as='span' weight='semibold'>
              {name}
            </Text>{' '}
            {usageCount === 1
              ? t(
                  'can’t be deleted because it is currently in use. The Call Group must be removed from the following place in order to be deleted.'
                )
              : t(
                  'can’t be deleted because it is currently in use. The Call Group must be removed from the following places in order to be deleted.'
                )}
          </Text>
        </Trans>
      }
    />
  );
};
